import { Component, Input } from '@angular/core';
import { AdminOverviewResult, AdminTopOrganization } from '../../../models/admin-overview.model';
import { SourceSlice, sourceSlices, sourceSlicesLabel } from '../../../utils/source-split';

/** Direction of a change against the previous window. `text` never carries
 *  the arrow, so screen readers get words, not glyphs. */
export interface Delta { dir: 'up' | 'down' | 'flat'; text: string; }

/** "Usage and spend" card: the window's totals, each compared against
 *  `current.previous` (the window right before it), then the organizations
 *  ranked by spend. `previous` is null when the platform is younger than
 *  the window; the comparisons are then simply left out. */
@Component({
  selector: 'app-usage-spend-card',
  standalone: false,
  templateUrl: './usage-spend-card.html',
})
export class UsageSpendCard {
  @Input({ required: true }) current!: AdminOverviewResult;
  @Input() rangeDays = 30;

  get hasComparison(): boolean {
    return !!this.current.previous;
  }

  get currency(): string {
    return this.current.wallets.currency ?? 'eur';
  }

  get sources(): SourceSlice[] {
    return sourceSlices(this.current.usage.bySource);
  }

  get sourcesLabel(): string {
    return sourceSlicesLabel(this.sources);
  }

  /** Share of the window's total cost, 0-100. */
  share(o: AdminTopOrganization): number {
    const total = this.current.usage.costCents;
    return total > 0 ? Math.round((o.costCents / total) * 100) : 0;
  }

  /** organization already carries its own balance now (same shape as an
   *  Organizations list row), no separate lookup needed. Kept as a method
   *  (not a template-inlined field) since exportCsv() also calls it. */
  balanceCents(o: AdminTopOrganization): number {
    return o.organization.balanceCents;
  }

  // ── Comparisons ───────────────────────────────────────────────

  /** "12.0% from 1,247". `from` is the formatted previous value; leave it
   *  out where the column already says what the number is. */
  private pct(now: number, before: number, from?: string): Delta {
    if (before === now) return { dir: 'flat', text: 'No change' };
    if (before === 0) return { dir: 'up', text: 'None before' };
    const change = ((now - before) / before) * 100;
    return { dir: change > 0 ? 'up' : 'down', text: `${Math.abs(change).toFixed(1)}%${from ? ` from ${from}` : ''}` };
  }

  private static count(n: number): string {
    return n.toLocaleString('en-US');
  }

  private static money(cents: number): string {
    return (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  get requestsDelta(): Delta | null {
    const before = this.current.previous?.usage;
    return before ? this.pct(this.current.usage.requests, before.requests, UsageSpendCard.count(before.requests)) : null;
  }

  get creditsDelta(): Delta | null {
    const before = this.current.previous?.usage;
    return before ? this.pct(this.current.usage.credits, before.credits, UsageSpendCard.count(before.credits)) : null;
  }

  get costDelta(): Delta | null {
    const before = this.current.previous?.usage;
    return before ? this.pct(this.current.usage.costCents, before.costCents, UsageSpendCard.money(before.costCents)) : null;
  }

  get errorsDelta(): Delta | null {
    const before = this.current.previous?.usage;
    if (!before) return null;
    const diff = this.current.usage.errors - before.errors;
    if (diff === 0) return { dir: 'flat', text: 'Same as before' };
    return { dir: diff > 0 ? 'up' : 'down', text: `${UsageSpendCard.count(Math.abs(diff))} ${diff > 0 ? 'more' : 'fewer'} than before` };
  }

  get latencyDelta(): Delta | null {
    const before = this.current.previous?.usage;
    if (!before || !before.requests) return null;
    const diff = Math.round(this.current.usage.avgLatencyMs - before.avgLatencyMs);
    if (diff === 0) return { dir: 'flat', text: 'Same as before' };
    return { dir: diff > 0 ? 'up' : 'down', text: `${UsageSpendCard.count(Math.abs(diff))} ms ${diff > 0 ? 'slower' : 'faster'}` };
  }

  /** Per organization: cost change against its own spend in the previous
   *  window, matched by slug, or "New" when it was not in that window's
   *  ranking at all. */
  orgDelta(o: AdminTopOrganization): Delta | null {
    const before = this.current.previous?.topOrganizations.find(p => p.organization.slug === o.organization.slug);
    if (!this.hasComparison) return null;
    if (!before || before.costCents === 0) return { dir: 'flat', text: 'New' };
    return this.pct(o.costCents, before.costCents);
  }

  get totalCostDelta(): Delta | null {
    const before = this.current.previous?.usage;
    return before ? this.pct(this.current.usage.costCents, before.costCents) : null;
  }

  /** Client-side CSV of exactly what the table shows; no export endpoint exists. */
  exportCsv(): void {
    const cur = this.currency.toUpperCase();
    const header = `Rank,Organization,Requests,Credits,Share of spend (%),Cost (${cur}),Balance (${cur})\n`;
    const rows = this.current.topOrganizations.map((o, i) => [
      i + 1,
      `"${o.organization.name.replace(/"/g, '""')}"`,
      o.requests,
      o.credits,
      this.share(o),
      (o.costCents / 100).toFixed(2),
      (this.balanceCents(o) / 100).toFixed(2),
    ].join(',')).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'organizations-by-spend.csv';
    a.click();
    URL.revokeObjectURL(url);
  }
}
